import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef
} from '@angular/core';

import { FocusHelper } from '../../helpers/focus.helper';
import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';


/**
 * ### Accessibility Usage
 * + ***ariaLabelledBy*** - use this to link an external label to the chip list
 * + ***clearAllChipsTootlip*** - use this to provide a tooltip for the clear all chips button
 * + ***handleFocus*** - let the component handle focus after removing a chip. Default is true
 */
@Component({
    selector: 'agpr-chips',
    templateUrl: './chips.component.html',
    styleUrls: ['./chips.component.scss'],
    providers: [getProvider(ChipsComponent)],
    standalone: false
})
export class ChipsComponent extends ControlValueAccessorModel<any[]>{
    @Input() id: string = null;
    @Input() labelFieldName: string;
    @Input() disabled: boolean = false;
    @Input() ignorePadding: boolean = false;
    @Input() clearAllChipsButton: boolean = true;
    @Input() clearAllChipsTooltip: string = 'Core.button.clearAll';
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() ariaLabelledBy: string = null;
    @Input() handleFocus: boolean = true;
    @Input() useLink: boolean = false;
    @Input() linkFieldName: string;
    @Input() placeholder: string;

    @Output() allChipsRemoved: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() chipRemoved: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('chipList') chipList: ElementRef;

    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly buttonTypes = ButtonTypesEnum;

    writeValue(value: any[]): void {
        this.value = value ?? [];
    }

    addChip(chip: any): void {
        this.value.push(chip);
        this.onModelChange(this.value);
    }

    removeChip(chip: any): void {
        const index = this.value.indexOf(chip);
        this.value.splice(index, 1);
        if (this.value.length === 0) {
            this.allChipsRemoved.emit(true);
        } else if (this.handleFocus) {
            setTimeout(() => FocusHelper.handleFocusAfterChipRemoval(this.chipList.nativeElement, index, 'button'));
        } else {
            this.chipRemoved.emit(index);
        }
        this.onModelChange(this.value);
    }

    removeAllChips(): void {
        this.allChipsRemoved.emit(true);
        this.value = [];
        this.onModelChange(this.value);
    }
}
